/**
 * /owner/templates — เลือกชุดค่าตั้งต้น (Template) ตามประเภทธุรกิจ.
 *
 * 2 ขั้น: เลือกขนาดบริษัท → เลือก template → ระบบสร้างค่าตั้งต้นทุกอย่างให้
 * (สิทธิ์การลา · เงินเพิ่ม · เงินหัก · กะ · คอมมิชชั่น · ค่าพื้นฐาน).
 *
 * Reset 2 ระดับ:
 *   - ล้างค่าตั้งต้น (เบา)  → ลบเฉพาะ master data, พนักงานคงอยู่
 *   - ล้างทั้งหมด (หนัก) → ลบพนักงาน + คำขอ + เช็คอินด้วย (ประวัติเงินเดือนคงอยู่)
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sprout, Flower, TreeDeciduous, Building2, RotateCcw, Trash2, ChevronLeft, Check, AlertTriangle } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { apiFetch, ApiError } from '../../lib/api-client';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';

type ScaleId = 'tiny' | 'small' | 'medium' | 'large';

const SCALE_RANGES: Array<{ id: ScaleId; label: string; desc: string; Icon: typeof Sprout }> = [
  { id: 'tiny',   label: '1-10 คน',   desc: 'เริ่มต้น · ทีมเล็ก',                    Icon: Sprout },
  { id: 'small',  label: '11-30 คน',  desc: 'SME ทั่วไป',                          Icon: Flower },
  { id: 'medium', label: '31-50 คน',  desc: 'SME ขนาดกลาง · มีหัวหน้าหลายคน',   Icon: TreeDeciduous },
  { id: 'large',  label: '51-100 คน', desc: 'SME ขนาดใหญ่ · หลายแผนก',          Icon: Building2 },
];

interface TemplateSummary {
  id: string;
  name: string;
  tagline: string;
  icon: string;
  scale_min: number;
  scale_max: number;
  meta: boolean;
  preview: {
    leave_packages: number;
    allowances: number;
    deductions: number;
    shifts: number;
    commissions: number;
    settings: number;
  };
}

export function OwnerTemplates() {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const [scale, setScale] = useState<ScaleId | null>(null);
  const [list, setList] = useState<TemplateSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState<string | null>(null);

  useEffect(() => {
    if (!scale) return;
    setLoading(true);
    apiFetch<{ industries: TemplateSummary[] }>(`/templates?scale=${scale}`)
      .then((r) => setList(r.industries))
      .catch((e) => toast.error(e instanceof Error ? e.message : 'โหลดไม่สำเร็จ'))
      .finally(() => setLoading(false));
  }, [scale, toast]);

  async function handleApply(t: TemplateSummary, force: boolean) {
    if (!(await confirm({
      title: `ใช้ template "${t.name}"?`,
      message: force
        ? 'ระบบจะ "ล้างค่าตั้งต้นเดิม" ของบริษัทออกก่อน แล้วใส่ของใหม่ตาม template นี้. พนักงานและประวัติเงินเดือนคงอยู่.'
        : 'ระบบจะสร้างค่าตั้งต้นทุกอย่างให้อัตโนมัติ (สิทธิ์การลา / เงินเพิ่ม / เงินหัก / กะ / ค่าพื้นฐาน).',
      confirmText: force ? 'ล้างของเดิม + ใช้' : 'ใช้ template',
      danger: force,
    }))) return;

    setApplying(t.id);
    try {
      const r = await apiFetch<{ template_id: string; counts: Record<string, number> }>('/templates/apply', {
        method: 'POST',
        body: { template_id: t.id, force },
      });
      const n = (key: string) => r.counts[key] ?? 0;
      const lines = [
        n('leave_packages') > 0 ? `สิทธิ์การลา ${n('leave_packages')} ชุด` : null,
        n('allowances') > 0 ? `เงินเพิ่ม ${n('allowances')} รายการ` : null,
        n('deductions') > 0 ? `เงินหัก ${n('deductions')} รายการ` : null,
        n('shifts') > 0 ? `กะ ${n('shifts')} กะ` : null,
        n('commissions') > 0 ? `คอม ${n('commissions')} แพ็คเกจ` : null,
        n('settings') > 0 ? `ค่าพื้นฐาน ${n('settings')} ค่า` : null,
      ].filter(Boolean).join(' · ');
      toast.success(`ใช้งานเรียบร้อย — ${lines}`);
      navigate('/owner/settings');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'DUPLICATE' && !force) {
        const ok = await confirm({
          title: 'มีค่าตั้งต้นเดิมอยู่แล้ว',
          message: 'ต้องการล้างของเดิมก่อนใช้ template ใหม่ใช่ไหม? พนักงานและประวัติเงินเดือนคงอยู่.',
          confirmText: 'ล้างของเดิม + ใช้',
          danger: true,
        });
        if (ok) await handleApply(t, true);
      } else {
        toast.error(e instanceof Error ? e.message : 'ใช้ template ไม่สำเร็จ');
      }
    } finally {
      setApplying(null);
    }
  }

  async function handleSoftReset() {
    if (!(await confirm({
      title: 'ล้างค่าตั้งต้น (เบา)?',
      message: 'จะลบ สิทธิ์การลา / เงินเพิ่ม / เงินหัก / กะ / คอมมิชชั่น ของบริษัททิ้งหมด. พนักงานและประวัติเงินเดือนคงอยู่. เหมาะตอนอยากเปลี่ยน template ใหม่.',
      confirmText: 'ล้างค่าตั้งต้น',
      danger: true,
    }))) return;
    try {
      await apiFetch('/templates/reset-soft', { method: 'POST' });
      toast.success('ล้างค่าตั้งต้นเรียบร้อย — เลือก template ใหม่ได้เลย');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ล้างไม่สำเร็จ');
    }
  }

  async function handleHardReset() {
    // 2-step confirm — ขั้นแรกเตือน, ขั้นสองให้พิมพ์ DELETE-ALL
    if (!(await confirm({
      title: '⚠️ ล้างทั้งหมด (หนัก)',
      message: 'จะลบ พนักงานทั้งหมด + คำขอลา/OT + เช็คอิน + ตั๋ว + ค่าตั้งต้นทั้งหมด. ประวัติการจ่ายเงินเดือนเก่าจะถูกเก็บไว้ตามกฎหมาย แต่ทุกอย่างอื่นจะหายหมด. การลบนี้ย้อนกลับไม่ได้.',
      confirmText: 'เข้าใจแล้ว — ดำเนินการต่อ',
      danger: true,
    }))) return;

    if (!(await confirm({
      title: 'แน่ใจอีกครั้งนะ?',
      message: 'การลบนี้ย้อนกลับไม่ได้จริงๆ. ถ้าแน่ใจ กดปุ่ม "ลบทั้งหมด" ด้านล่าง.',
      confirmText: 'ลบทั้งหมด',
      danger: true,
    }))) return;

    try {
      await apiFetch('/templates/reset-hard', {
        method: 'POST',
        body: { confirm: 'DELETE-ALL' },
      });
      toast.success('ล้างทั้งหมดเรียบร้อย — บริษัทกลับสู่สถานะเริ่มต้น');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ล้างไม่สำเร็จ');
    }
  }

  // ----- Phase 1: pick scale -----
  if (!scale) {
    return (
      <AdminLayout title="เริ่มต้นด้วย Template">
        <div className="card p-5 mb-4">
          <h2 className="font-semibold mb-1">บริษัทคุณมีพนักงานกี่คน?</h2>
          <p className="text-xs text-gray-500 mb-4">เลือกช่วงที่ใกล้สุด — ระบบจะแนะนำ template ที่เหมาะกับขนาดนี้</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {SCALE_RANGES.map((r) => (
              <button
                key={r.id}
                onClick={() => setScale(r.id)}
                className="text-left bg-gray-50 border border-gray-200 rounded-xl p-4 hover:border-brand-400 hover:shadow-sm transition"
              >
                <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center mb-2">
                  <r.Icon className="w-5 h-5" />
                </div>
                <div className="font-semibold">{r.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{r.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Reset zones */}
        <div className="card p-5 mb-3 border-amber-200 bg-amber-50/40">
          <div className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
            <RotateCcw className="w-4 h-4" />
            ล้างค่าตั้งต้น (เบา) — ใช้กรณีเปลี่ยน template
          </div>
          <p className="text-xs text-gray-700 mb-3">
            ลบเฉพาะค่าตั้งต้นต่างๆ (สิทธิ์การลา / เงินเพิ่ม / เงินหัก / กะ / คอมมิชชั่น).
            พนักงานและประวัติเงินเดือน <b>คงอยู่ครบ</b>.
          </p>
          <button onClick={handleSoftReset} className="btn-secondary text-sm text-amber-800 border-amber-300">
            <RotateCcw className="w-4 h-4" /> ล้างค่าตั้งต้น
          </button>
        </div>

        <div className="card p-5 border-red-200 bg-red-50/40">
          <div className="font-semibold text-red-700 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            ล้างทั้งหมด (หนัก) — เริ่มต้นใหม่ทุกอย่าง
          </div>
          <p className="text-xs text-gray-700 mb-3">
            ลบ <b>พนักงานทั้งหมด</b> + คำขอลา/OT + เช็คอิน + ตั๋ว + ค่าตั้งต้น.
            ประวัติการจ่ายเงินเดือนเก่ายังเก็บไว้ตามกฎหมาย แต่ทุกอย่างอื่นหายหมด.
            <span className="block text-red-700 font-medium mt-1">การลบนี้ย้อนกลับไม่ได้</span>
          </p>
          <button onClick={handleHardReset} className="btn-danger text-sm">
            <Trash2 className="w-4 h-4" /> ล้างทั้งหมด
          </button>
        </div>
      </AdminLayout>
    );
  }

  // ----- Phase 2: pick template -----
  return (
    <AdminLayout title="เลือก Template">
      <button onClick={() => { setScale(null); setList(null); }} className="text-sm text-brand-700 mb-3 flex items-center gap-1 hover:underline">
        <ChevronLeft className="w-4 h-4" /> เปลี่ยนขนาดบริษัท
      </button>

      {loading || !list ? (
        <Loading />
      ) : list.length === 0 ? (
        <p className="text-center text-gray-500 py-8">ไม่พบ template สำหรับขนาดนี้</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {list.map((t) => (
            <div key={t.id} className="card p-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center font-bold shrink-0">
                  {t.name[0]}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{t.tagline}</div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-1.5 text-[11px] mb-3">
                <Stat label="สิทธิ์การลา" n={t.preview.leave_packages} />
                <Stat label="เงินเพิ่ม" n={t.preview.allowances} />
                <Stat label="เงินหัก" n={t.preview.deductions} />
                <Stat label="กะ" n={t.preview.shifts} />
                <Stat label="คอม" n={t.preview.commissions} />
                <Stat label="ค่าพื้นฐาน" n={t.preview.settings} />
              </div>

              <button
                onClick={() => handleApply(t, false)}
                disabled={applying === t.id}
                className="btn-primary w-full text-sm"
              >
                <Check className="w-4 h-4" />
                {applying === t.id ? 'กำลังใช้งาน…' : `ใช้ template นี้`}
              </button>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

function Stat({ label, n }: { label: string; n: number }) {
  return (
    <div className={`rounded-lg px-2 py-1.5 ${n > 0 ? 'bg-brand-50 text-brand-700' : 'bg-gray-50 text-gray-400'}`}>
      <div className="font-semibold tabular-nums">{n}</div>
      <div className="text-[10px]">{label}</div>
    </div>
  );
}
